import { useCallback, useEffect, useState } from 'react';
import type { ApiKey, RelationListItem } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PlaintextSecretDialog } from '@/components/PlaintextSecretDialog';
import { hub } from '@/lib/hub-client';
import { selfRegistration, SelfRegistrationStatus } from './track-self-registration';

// HUB-089: a track's deploy credential is an API key bound one-to-one via
// `tf_api_key.track_id`. The bound key is what lets the deployed pod push its
// manifest on every boot. This card shows the binding, repairs a missing one
// (Assign — the silent-403 fix for rows first created by a human/seed
// principal), and rotates a suspected-leaked key (revoke + mint-and-bind,
// plaintext shown once).
export function TrackApiKeyCard({ trackId }: { trackId: string }) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [relations, setRelations] = useState<RelationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [revealed, setRevealed] = useState<{ name: string; plaintext: string } | null>(null);

  const reload = useCallback(() => {
    return Promise.all([
      hub.apiKeysList(),
      hub.relationsList({ query: { objectType: 'track', objectId: trackId, relation: 'admin', includeStructural: false, limit: 500 } }),
    ])
      .then(([k, r]) => {
        setKeys(k.apiKeys);
        setRelations(r.relations);
      })
      .catch((err) => setError((err as Error).message));
  }, [trackId]);

  useEffect(() => {
    void reload().finally(() => setLoading(false));
  }, [reload]);

  const status = selfRegistration(trackId, keys, relations);
  const bound = status.ok && status.via === 'bound' ? status.key : null;
  // A track credential must be an unbound machine key: no user, no track, live.
  const assignable = keys.filter((k) => !k.userId && !k.trackId && !k.revokedAt);

  const assign = async () => {
    if (!selectedKeyId) return;
    setBusy(true);
    setError(null);
    try {
      await hub.tracksApiKeyAssign({ params: { id: trackId }, body: { keyId: selectedKeyId } });
      setSelectedKeyId('');
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setBusy(true);
    setError(null);
    try {
      const minted = await hub.tracksApiKeyRotate({ params: { id: trackId } });
      setConfirmRotate(false);
      setRevealed({ name: minted.name, plaintext: minted.plaintext });
      await reload();
    } catch (err) {
      setError((err as Error).message);
      setConfirmRotate(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">API key</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The key the deployed track authenticates with. It may push this track's manifest on
          boot when it is bound to the track or holds the track's admin relation.
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <SelfRegistrationStatus state={status} />
        )}
        {loading ? null : bound ? (
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setConfirmRotate(true)} disabled={busy}>
              Rotate key
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="track-api-key">Bind an existing key</Label>
              <div className="flex gap-2">
                <Select
                  id="track-api-key"
                  className="w-full"
                  value={selectedKeyId}
                  onChange={(e) => setSelectedKeyId(e.target.value)}
                  disabled={busy || assignable.length === 0}
                >
                  <option value="">
                    {assignable.length === 0 ? 'No unbound machine keys available' : 'Select a key…'}
                  </option>
                  {assignable.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name} ({k.prefix}…)
                    </option>
                  ))}
                </Select>
                <Button onClick={() => void assign()} disabled={busy || !selectedKeyId}>
                  Assign
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Or mint a fresh bound key: <Button variant="outline" size="sm" onClick={() => setConfirmRotate(true)} disabled={busy}>Mint &amp; bind</Button>
            </p>
          </div>
        )}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </CardContent>
      <ConfirmDialog
        open={confirmRotate}
        title={bound ? 'Rotate API key?' : 'Mint a bound API key?'}
        description={
          bound
            ? `The current key "${bound.name}" is revoked immediately — the deployed track keeps failing until its settings carry the new key.`
            : 'A new key is minted and bound to this track. Put it in the track’s deployment settings.'
        }
        confirmLabel={bound ? 'Rotate' : 'Mint'}
        destructive={Boolean(bound)}
        busy={busy}
        onConfirm={() => void rotate()}
        onCancel={() => setConfirmRotate(false)}
      />
      <PlaintextSecretDialog
        open={revealed !== null}
        title="New track API key"
        description={revealed ? `Key "${revealed.name}" is bound to this track.` : ''}
        secret={revealed?.plaintext ?? null}
        onClose={() => setRevealed(null)}
      />
    </Card>
  );
}
